import React, { useState, useEffect, useCallback } from 'react';
import * as Icons from 'lucide-react';

const API_BASE = window.location.hostname === 'localhost' ? 'http://localhost:8092' : `${window.location.protocol}//${window.location.hostname}:8092`;

const WABAManagement = () => {
  const [activeTab, setActiveTab] = useState('accounts');
  const [accounts, setAccounts] = useState([]);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [techProvider, setTechProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/wa/admin/waba-accounts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAccounts(data.accounts || []);
      }
    } catch (err) {
      console.error('Failed to fetch WABA accounts:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPhoneNumbers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/wa/admin/phone-numbers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPhoneNumbers(data.phone_numbers || []);
      }
    } catch (err) {
      console.error('Failed to fetch phone numbers:', err);
    }
  }, []);

  const fetchTechProvider = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/wa/admin/tech-provider`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTechProvider(data.provider || null);
      }
    } catch (err) {
      console.error('Failed to fetch tech provider:', err);
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
    fetchPhoneNumbers();
    fetchTechProvider();
  }, [fetchAccounts, fetchPhoneNumbers, fetchTechProvider]);

  const statusColors = {
    active: { bg: '#dcfce7', text: '#166534' },
    inactive: { bg: '#f3f4f6', text: '#374151' },
    suspended: { bg: '#fee2e2', text: '#991b1b' },
    pending_review: { bg: '#fef3c7', text: '#92400e' },
    banned: { bg: '#fee2e2', text: '#991b1b' },
  };

  const qualityColors = {
    GREEN: { bg: '#dcfce7', text: '#166534' },
    YELLOW: { bg: '#fef3c7', text: '#92400e' },
    RED: { bg: '#fee2e2', text: '#991b1b' },
    UNKNOWN: { bg: '#f3f4f6', text: '#374151' },
  };

  const tierLabels = {
    TIER_1K: '1K/day',
    TIER_10K: '10K/day',
    TIER_100K: '100K/day',
    UNLIMITED: 'Unlimited',
  };

  const filteredAccounts = accounts.filter(a =>
    !searchTerm ||
    a.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.waba_id?.includes(searchTerm) ||
    a.business_id?.includes(searchTerm)
  );

  const tabs = [
    { key: 'accounts', label: 'WABA Accounts', icon: 'Building2' },
    { key: 'phones', label: 'Phone Numbers', icon: 'Phone' },
    { key: 'provider', label: 'Tech Provider Config', icon: 'Settings' },
    { key: 'signup', label: 'Embedded Signup', icon: 'UserPlus' },
  ];

  const getIcon = (name, size = 18) => {
    const Icon = Icons[name];
    return Icon ? <Icon style={{ width: size, height: size }} /> : null;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
              <Icons.Building2 className="w-5 h-5 text-white" />
            </div>
            WABA Account Management
          </h1>
          <p className="text-sm text-gray-500 mt-1">Manage all WhatsApp Business Accounts across the platform</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-white font-medium text-sm transition-all hover:shadow-lg"
          style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}
        >
          <Icons.Plus className="w-4 h-4" />
          Add WABA Account
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Total Accounts', value: accounts.length || '0', icon: 'Building2', color: '#25D366' },
          { label: 'Active', value: accounts.filter(a => a.status === 'active').length || '0', icon: 'CheckCircle', color: '#128C7E' },
          { label: 'Phone Numbers', value: phoneNumbers.length || '0', icon: 'Phone', color: '#34B7F1' },
          { label: 'Pending Review', value: accounts.filter(a => a.status === 'pending_review').length || '0', icon: 'Clock', color: '#F59E0B' },
        ].map((stat) => (
          <div key={stat.label} className="bg-white rounded-xl p-4 border border-gray-100 hover:shadow-md transition-all">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${stat.color}14`, color: stat.color }}>
                {getIcon(stat.icon, 20)}
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-xs text-gray-500">{stat.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="flex border-b border-gray-100 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className="flex items-center gap-2 px-5 py-3.5 text-sm font-medium transition-all border-b-2 whitespace-nowrap"
              style={{
                borderColor: activeTab === tab.key ? '#25D366' : 'transparent',
                color: activeTab === tab.key ? '#25D366' : '#6b7280',
                background: activeTab === tab.key ? 'rgba(37, 211, 102, 0.04)' : 'transparent',
              }}
            >
              {getIcon(tab.icon, 16)}
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {/* WABA Accounts Tab */}
          {activeTab === 'accounts' && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="relative flex-1 max-w-md">
                  <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by name, WABA ID, or Business ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-400"
                  />
                </div>
                <button onClick={fetchAccounts} className="p-2.5 rounded-xl border border-gray-200 hover:bg-gray-50 text-gray-500">
                  <Icons.RefreshCw className="w-4 h-4" />
                </button>
              </div>

              {loading ? (
                <div className="text-center py-12">
                  <Icons.Loader2 className="w-8 h-8 animate-spin mx-auto text-green-500 mb-3" />
                  <p className="text-sm text-gray-500">Loading WABA accounts...</p>
                </div>
              ) : filteredAccounts.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(37, 211, 102, 0.08)' }}>
                    <Icons.Building2 className="w-8 h-8" style={{ color: '#25D366' }} />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">No WABA Accounts</h3>
                  <p className="text-sm text-gray-500 mb-4">No WhatsApp Business Accounts have been registered yet.</p>
                  <p className="text-xs text-gray-400">Users can onboard via Embedded Signup or accounts can be added manually.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredAccounts.map((account) => (
                    <div key={account.id || account.waba_id} className="p-5 rounded-xl border border-gray-100 hover:border-green-200 hover:shadow-sm transition-all">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
                            <Icons.MessageCircle className="w-7 h-7 text-white" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">{account.name || 'Unnamed Account'}</h3>
                            <p className="text-sm text-gray-500">WABA ID: {account.waba_id || 'N/A'}</p>
                            <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                              <span>User ID: {account.user_id}</span>
                              <span>Business ID: {account.business_id || 'N/A'}</span>
                              <span>Tier: {tierLabels[account.messaging_limit_tier] || 'N/A'}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="px-2.5 py-1 rounded-lg text-xs font-semibold" style={{
                            background: qualityColors[account.quality_rating]?.bg || '#f3f4f6',
                            color: qualityColors[account.quality_rating]?.text || '#374151',
                          }}>
                            Quality: {account.quality_rating || 'UNKNOWN'}
                          </span>
                          <span className="px-2.5 py-1 rounded-lg text-xs font-semibold" style={{
                            background: statusColors[account.status]?.bg || '#f3f4f6',
                            color: statusColors[account.status]?.text || '#374151',
                          }}>
                            {(account.status || 'unknown').replace('_', ' ').toUpperCase()}
                          </span>
                          <div className="flex items-center gap-1">
                            <button className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600" title="Edit">
                              <Icons.Pencil className="w-4 h-4" />
                            </button>
                            <button className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600" title="View Details">
                              <Icons.Eye className="w-4 h-4" />
                            </button>
                            <button className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500" title="Suspend">
                              <Icons.Ban className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Phone Numbers Tab */}
          {activeTab === 'phones' && (
            <div className="space-y-4">
              {phoneNumbers.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(37, 211, 102, 0.08)' }}>
                    <Icons.Phone className="w-8 h-8" style={{ color: '#25D366' }} />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">No Phone Numbers</h3>
                  <p className="text-sm text-gray-500">Phone numbers will appear here once WABA accounts are connected.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-xs text-gray-500 border-b border-gray-100">
                        <th className="text-left px-4 py-3 font-medium">Phone Number</th>
                        <th className="text-left px-4 py-3 font-medium">Verified Name</th>
                        <th className="text-left px-4 py-3 font-medium">WABA Account</th>
                        <th className="text-center px-4 py-3 font-medium">Quality</th>
                        <th className="text-center px-4 py-3 font-medium">Platform</th>
                        <th className="text-center px-4 py-3 font-medium">Status</th>
                        <th className="text-center px-4 py-3 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {phoneNumbers.map((phone) => (
                        <tr key={phone.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{phone.display_phone_number}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{phone.verified_name || 'N/A'}</td>
                          <td className="px-4 py-3 text-xs text-gray-500">{phone.waba_account_id}</td>
                          <td className="px-4 py-3 text-center">
                            <span className="px-2 py-0.5 rounded-md text-xs font-medium" style={{
                              background: qualityColors[phone.quality_rating]?.bg,
                              color: qualityColors[phone.quality_rating]?.text,
                            }}>
                              {phone.quality_rating || 'UNKNOWN'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center text-xs text-gray-500">{phone.platform_type || 'CLOUD_API'}</td>
                          <td className="px-4 py-3 text-center">
                            <span className="px-2 py-0.5 rounded-md text-xs font-medium" style={{
                              background: statusColors[phone.status]?.bg,
                              color: statusColors[phone.status]?.text,
                            }}>
                              {(phone.status || 'pending').toUpperCase()}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600">
                              <Icons.Settings className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Tech Provider Config Tab */}
          {activeTab === 'provider' && (
            <div className="max-w-3xl space-y-6">
              <div className="p-4 rounded-xl bg-blue-50 border border-blue-100">
                <h4 className="text-sm font-medium text-blue-800 mb-1 flex items-center gap-2">
                  <Icons.Info className="w-4 h-4" />
                  Tech Provider Configuration
                </h4>
                <p className="text-xs text-blue-700">
                  Configure your Meta Tech Provider credentials. These are used for Embedded Signup, managing WABA accounts, and API access across all users.
                  Refer to <a href="https://developers.facebook.com/docs/whatsapp/cloud-api/get-started" target="_blank" rel="noopener noreferrer" className="underline">Meta's documentation</a>.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Facebook App ID</label>
                  <input
                    type="text"
                    defaultValue={techProvider?.app_id || ''}
                    placeholder="Enter Facebook App ID"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Facebook App Secret</label>
                  <input
                    type="password"
                    defaultValue={techProvider?.app_secret ? '••••••••' : ''}
                    placeholder="Enter App Secret"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Meta Business ID</label>
                  <input
                    type="text"
                    defaultValue={techProvider?.business_id || ''}
                    placeholder="Enter Meta Business ID"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">API Version</label>
                  <select
                    defaultValue={techProvider?.api_version || 'v20.0'}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-400"
                  >
                    <option>v20.0</option>
                    <option>v19.0</option>
                    <option>v18.0</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">System User Access Token</label>
                <div className="flex gap-2">
                  <input
                    type="password"
                    defaultValue={techProvider?.system_user_token ? '••••••••' : ''}
                    placeholder="Enter System User Access Token"
                    className="flex-1 px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-400"
                  />
                  <button className="px-3 py-2 rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50">
                    <Icons.Eye className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Webhook URL</label>
                  <input
                    type="url"
                    defaultValue={techProvider?.webhook_url || ''}
                    placeholder="https://your-domain.com/api/wa/webhook"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Webhook Verify Token</label>
                  <input
                    type="text"
                    defaultValue={techProvider?.webhook_verify_token || ''}
                    placeholder="Enter verify token"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-400"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Configuration Token (Embedded Signup)</label>
                  <input
                    type="text"
                    defaultValue={techProvider?.config_token || ''}
                    placeholder="Enter config token"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Solution ID (Multi-Partner)</label>
                  <input
                    type="text"
                    defaultValue={techProvider?.solution_id || ''}
                    placeholder="Enter solution ID"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-400"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button className="px-5 py-2.5 rounded-xl text-white font-medium text-sm hover:shadow-lg transition-all" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
                  Save Configuration
                </button>
                <button className="px-5 py-2.5 rounded-xl text-gray-600 font-medium text-sm border border-gray-200 hover:bg-gray-50">
                  Test Connection
                </button>
              </div>
            </div>
          )}

          {/* Embedded Signup Tab */}
          {activeTab === 'signup' && (
            <div className="space-y-6">
              <div className="p-4 rounded-xl bg-green-50 border border-green-100">
                <h4 className="text-sm font-medium text-green-800 mb-1 flex items-center gap-2">
                  <Icons.UserPlus className="w-4 h-4" />
                  Embedded Signup Flow
                </h4>
                <p className="text-xs text-green-700">
                  Users onboard their WhatsApp Business Account through Meta's Embedded Signup. This flow handles Facebook Login, WABA creation, phone number registration, and token exchange automatically.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { step: '1', title: 'Facebook Login', desc: 'User authenticates with Facebook and grants permissions for WhatsApp Business', icon: 'LogIn', color: '#4267B2' },
                  { step: '2', title: 'WABA Setup', desc: 'User creates or selects a WhatsApp Business Account and registers a phone number', icon: 'Building2', color: '#25D366' },
                  { step: '3', title: 'Token Exchange', desc: 'System exchanges auth code for access token and subscribes to webhooks', icon: 'Key', color: '#128C7E' },
                ].map((item) => (
                  <div key={item.step} className="p-5 rounded-xl border border-gray-100 hover:shadow-sm transition-all">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold" style={{ background: item.color }}>
                        {item.step}
                      </div>
                      <h4 className="font-semibold text-gray-900">{item.title}</h4>
                    </div>
                    <p className="text-xs text-gray-500">{item.desc}</p>
                  </div>
                ))}
              </div>

              <div className="bg-white rounded-xl border border-gray-100 p-5">
                <h3 className="font-semibold text-gray-900 mb-4">Recent Signup Sessions</h3>
                <div className="text-center py-8">
                  <Icons.UserPlus className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">No signup sessions recorded yet.</p>
                  <p className="text-xs text-gray-400 mt-1">Sessions will appear here when users initiate the Embedded Signup flow.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WABAManagement;
